import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Radar, ChevronDown, Inbox, Building2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { todayLong } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { PageContainer, PageHeader, Segmented, Skeleton, EmptyState, InlineError } from "@/components/common";
import { OpportunityCard } from "@/components/intel/OpportunityCard";
import { MorningBrief } from "@/components/intel/MorningBrief";
import { useIntel } from "@/components/intel/context";
import { useMediaQuery } from "@/hooks/use-media-query";

const BRIEF_KEY = "mb_intel_brief_open";

function CardSkeleton() {
  return (
    <div className="surface-flat p-5">
      <div className="flex items-start gap-3.5"><Skeleton className="h-10 w-10 rounded-xl" /><div className="flex-1"><Skeleton className="h-4 w-1/3" /><Skeleton className="mt-2 h-3 w-1/4" /></div><Skeleton className="h-7 w-20 rounded-full" /></div>
      <Skeleton className="mt-5 h-3 w-24" /><Skeleton className="mt-2 h-5 w-3/4" /><Skeleton className="mt-3 h-3 w-full" /><Skeleton className="mt-1.5 h-3 w-[85%]" />
      <Skeleton className="mt-4 h-24 w-full" />
    </div>
  );
}

export default function IntelTodayPage() {
  const navigate = useNavigate();
  const intel = useIntel();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [kind, setKind] = useState("new_business");
  const [country, setCountry] = useState("all");
  const [segment, setSegment] = useState("all");
  const [limit, setLimit] = useState(8);
  const [briefOpen, setBriefOpen] = useState(() => localStorage.getItem(BRIEF_KEY) !== "0");

  const load = useCallback(async (silent = false) => {
    if (!silent) setError(null);
    try {
      const params = { kind, limit, country: country === "all" ? "" : country, segment: segment === "all" ? "" : segment };
      const r = await api.get("/intel/today", { params });
      setData(r.data);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [kind, limit, country, segment]);

  useEffect(() => { load(); }, [load, intel.version]);

  const refresh = async () => {
    setRefreshing(true);
    await load(true);
    setRefreshing(false);
  };

  const toggleBrief = (v) => { setBriefOpen(v); localStorage.setItem(BRIEF_KEY, v ? "1" : "0"); };

  const feed = useMemo(() => (data?.feed || []).filter((v) => !intel.dismissed.has(v.project_id)), [data, intel.dismissed]);
  const kinds = useMemo(() => [{ key: "new_business", label: "New business" }, { key: "expansion", label: "Expansion" }, { key: "all", label: "All" }], []);
  const countries = data?.filters?.countries || [];
  const segments = data?.filters?.segments || [];

  return (
    <PageContainer wide data-testid="intel-today-page">
      <PageHeader
        eyebrow="Sales Intelligence"
        title="Today"
        description={<span>{todayLong()} · ranked new-business opportunities with evidence, what to sell and what to do next.</span>}
        actions={
          <>
            <Button variant="outline" className="gap-2" onClick={() => navigate("/intel/radar")} data-testid="today-open-radar-button"><Radar className="h-4 w-4" /> Project Radar</Button>
            <Button variant="outline" className="gap-2" onClick={refresh} disabled={refreshing} data-testid="today-refresh-button"><RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} /> Refresh</Button>
          </>
        }
      />

      {error ? <InlineError onRetry={() => load()} testId="today-error">{error}</InlineError> : null}

      {!isDesktop ? (
        <Collapsible open={briefOpen} onOpenChange={toggleBrief} className="mb-5">
          <CollapsibleTrigger asChild>
            <button type="button" className="flex w-full items-center justify-between rounded-xl border border-black/[0.06] bg-white px-4 py-3 text-left text-[13.5px] font-semibold" data-testid="today-brief-mobile-toggle">
              Morning brief <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", briefOpen && "rotate-180")} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3"><MorningBrief brief={data?.brief} feed={feed} /></CollapsibleContent>
        </Collapsible>
      ) : null}

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Segmented items={kinds} value={kind} onChange={(k) => { setKind(k); setLimit(8); }} testIdPrefix="today-kind" />
        <div className="flex flex-wrap items-center gap-2">
          <Select value={country} onValueChange={(v) => { setCountry(v); setLimit(8); }}>
            <SelectTrigger className="h-9 w-[150px] rounded-[10px] bg-white" data-testid="today-country-filter"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All countries</SelectItem>{countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={segment} onValueChange={(v) => { setSegment(v); setLimit(8); }}>
            <SelectTrigger className="h-9 w-[190px] rounded-[10px] bg-white" data-testid="today-segment-filter"><SelectValue placeholder="Segment" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All segments</SelectItem>{segments.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {data ? <span className="text-[12.5px] text-muted-foreground" data-testid="today-result-count"><span className="font-semibold tabular text-foreground">{data.total}</span> opportunities</span> : null}
        </div>
      </div>

      <div className="lg:grid lg:grid-cols-12 lg:gap-6">
        <div className="space-y-4 lg:col-span-8" data-testid="today-feed">
          {!data ? (
            <>{[0, 1, 2].map((i) => <CardSkeleton key={i} />)}</>
          ) : feed.length === 0 ? (
            <EmptyState icon={Inbox} title="No opportunities match these filters" description="Try another segment or country, or open the radar to see every detected signal." action={<Button onClick={() => { setKind("all"); setCountry("all"); setSegment("all"); }}>Clear filters</Button>} secondary={<Button variant="outline" onClick={() => navigate("/intel/radar")}>Open radar</Button>} testId="today-empty" />
          ) : (
            <>
              {feed.map((v, i) => <OpportunityCard key={v.project_id} view={v} rank={i + 1} />)}
              {data.total > feed.length ? (
                <div className="flex justify-center pt-2">
                  <Button variant="outline" onClick={() => setLimit((l) => l + 8)} data-testid="today-show-more-button">Show more <span className="text-muted-foreground">({data.total - feed.length} remaining)</span></Button>
                </div>
              ) : null}
            </>
          )}
        </div>
        {isDesktop ? (
          <div className="lg:col-span-4">
            <div className="sticky top-0 space-y-4">
              <MorningBrief brief={data?.brief} feed={feed} />
              <button type="button" onClick={() => navigate("/intel/companies")} className="surface-flat lift flex w-full items-center justify-between px-5 py-4 text-left" data-testid="today-open-companies-button">
                <span className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--muted))] text-muted-foreground"><Building2 className="h-4 w-4" /></span><span><span className="block text-[13.5px] font-semibold">All companies</span><span className="block text-[12px] text-muted-foreground">Browse and search the market map</span></span></span>
                <ChevronDown className="h-4 w-4 -rotate-90 text-muted-foreground" />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </PageContainer>
  );
}
